import React from 'react';
import {
    Box,
    IconButton,
    Skeleton,
    CircularProgress
} from '@mui/material';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import CommentInput, { type CommentInputRef } from '../commentInput';

interface Props {
    isSubmitting: boolean;
    isRecording: boolean;
    recordingTime: number;
    handleAudioRecording: () => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSubmitComment: () => void;
    commentInputRef: React.RefObject<CommentInputRef>;
    editingComment: any;
    isEditing: boolean;
    currentStatus: string;
}

const CommentInputFooter: React.FC<Props> = ({
    isSubmitting,
    isRecording,
    recordingTime,
    handleAudioRecording,
    handleFileChange,
    handleSubmitComment,
    commentInputRef,
    editingComment,
    isEditing,
    currentStatus
}) => {
    return (
        <Box
            sx={{
                borderTop: '1px solid #E5E7EB',
                pt: 2,
                mt: 'auto',
                backgroundColor: '#fff',
                opacity: currentStatus === "CLOSE" ? 0.5 : 1,
                pointerEvents: currentStatus === "CLOSE" ? 'none' : '',
            }}
            display="flex"
            gap={1}
            alignItems="flex-start"
        >
            {isSubmitting ? (
                <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 2 }} />
            ) : !isRecording ? (
                <CommentInput
                    ref={commentInputRef}
                    initialValue={isEditing && editingComment ? editingComment.comment : ''}
                    isSubmitting={isSubmitting}
                />
            ) : (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    width="100%"
                    height="120px"
                    sx={{
                        bgcolor: '#FFF5F5',
                        border: '2px dashed #EF4444',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                    }}
                >
                    <strong style={{ color: '#EF4444' }}>🎙️ Grabando audio...</strong>
                    <div style={{ color: '#EF4444', fontSize: '0.8rem', marginBottom: 4 }}>
                        Tiempo: {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                    </div>

                    <div className="audio-wave">
                        {[0, 0.2, 0.4, 0.6, 0.8].map((delay, i) => (
                            <div key={i} className="audio-wave-bar" style={{ animationDelay: `${delay}s` }}></div>
                        ))}
                    </div>
                </Box>
            )}

            <Box display="flex" flexDirection="column" gap={1}>
                <IconButton onClick={handleAudioRecording}>
                    {isSubmitting ? <CircularProgress size={20} thickness={5} /> : <MicIcon color={isRecording ? "error" : "inherit"} />}
                </IconButton>

                <label htmlFor="file-upload">
                    <input
                        id="file-upload"
                        type="file"
                        multiple
                        hidden
                        onChange={handleFileChange}
                    />
                    <IconButton component="span">
                        {isSubmitting ? <CircularProgress size={20} thickness={5} /> : <AttachFileIcon />}
                    </IconButton>
                </label>

                <IconButton
                    onClick={handleSubmitComment}
                    disabled={isSubmitting}
                    sx={{
                        bgcolor: '#E0E7FF',
                        color: '#7C3AED',
                        '&:hover': { bgcolor: '#DDD6FE' }
                    }}
                >
                    {isSubmitting ? <CircularProgress size={20} thickness={5} /> : <SendIcon />}
                </IconButton>
            </Box>
        </Box>
    );
};

export default CommentInputFooter;
